import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Megaphone, Save, BellPlus } from "lucide-react";

const PING_OPTS = [["everyone", "@everyone"], ["role", "Show Alerts role"], ["none", "No ping"]];
const KIND_LABEL = {
  new_show: "New show", premiere_week: "Premiere in 1 week", finale_week: "Finale in 1 week",
  special_week: "Special in 1 week", soon: "Starting soon", live: "LIVE now",
};
const FIELDS = ["show_alerts_channel_id", "show_alerts_role_id", "show_episode_reminders", "show_reminder_lead_minutes", "show_ping_new", "show_ping_premiere", "show_ping_episode"];
const etTime = (iso) => `${new Date(iso).toLocaleString("en-US", { timeZone: "America/New_York", weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} ET`;

function PingSelect({ label, value, onChange, testId }) {
  return (
    <label className="text-xs space-y-1">
      <span className="label-eyebrow block">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="px-3 py-2 text-sm w-full" data-testid={testId}>
        {PING_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}

function Activity({ data }) {
  if (!data) return null;
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div data-testid="show-alerts-upcoming">
        <div className="label-eyebrow mb-2">▮ COMING UP (7 DAYS) · {data.upcoming_total}</div>
        {data.upcoming.length === 0 ? <div className="text-xs text-[var(--ff-text-dim)]">Nothing scheduled to post this week.</div> : (
          <ul className="space-y-1 text-xs max-h-56 overflow-auto pr-1">
            {data.upcoming.map((u, i) => (
              <li key={i} className="flex gap-2"><span className="font-mono-ui text-[var(--ff-text-dim)] w-36 shrink-0">{etTime(u.post_at)}</span><span className="truncate">{KIND_LABEL[u.kind]} · {u.show_title}</span></li>
            ))}
          </ul>
        )}
      </div>
      <div data-testid="show-alerts-recent">
        <div className="label-eyebrow mb-2">▮ RECENT POSTS</div>
        {data.recent.length === 0 ? <div className="text-xs text-[var(--ff-text-dim)]">No posts yet.</div> : (
          <ul className="space-y-1 text-xs max-h-56 overflow-auto pr-1">
            {data.recent.map((r) => (
              <li key={r.key} className="flex gap-2" title={r.error || ""}>
                <span className={r.status === "posted" ? "text-emerald-400" : "text-[var(--ff-red)]"}>{r.status}</span>
                <span className="truncate">{KIND_LABEL[r.kind]} · {r.show_title}</span>
                <span className="ml-auto text-[var(--ff-text-dim)] shrink-0">{new Date(r.at).toLocaleDateString()}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// One Discord channel for new-show cards, 1-week premiere/finale/special reminders and episode reminders.
export default function ShowAlertsSettings() {
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(null);
  const [channels, setChannels] = useState([]);
  const [roles, setRoles] = useState([]);
  const [activity, setActivity] = useState(null);
  const loadActivity = useCallback(() => api.get("/admin/show-alerts").then(({ data }) => setActivity(data)).catch(() => {}), []);

  useEffect(() => {
    api.get("/admin/site-settings").then(({ data }) => {
      const f = Object.fromEntries(FIELDS.map((k) => [k, data[k]]));
      setForm(f); setSaved(f);
    }).catch(() => {});
    api.get("/admin/discord/channels").then(({ data }) => setChannels(data.text || [])).catch(() => {});
    api.get("/admin/discord/roles").then(({ data }) => setRoles(data.roles || [])).catch(() => {});
    loadActivity();
  }, [loadActivity]);

  if (!form) return null;
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const dirty = JSON.stringify(form) !== JSON.stringify(saved);
  const save = async () => {
    try {
      await api.put("/admin/site-settings", { ...form, show_reminder_lead_minutes: Number(form.show_reminder_lead_minutes) });
      setSaved(form); toast.success("Show announcement settings saved"); loadActivity();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const postPanel = async () => {
    try { await api.post("/admin/show-alerts/opt-in-panel"); toast.success("Opt-in button posted to Discord"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="surface-card p-6 space-y-5" data-testid="show-alerts-settings">
      <div className="flex items-start gap-3">
        <Megaphone size={20} className="text-[var(--ff-red)] shrink-0 mt-1" />
        <div>
          <div className="label-eyebrow mb-1">▮ DISCORD SHOW ANNOUNCEMENTS</div>
          <p className="text-sm text-[var(--ff-text-dim)] max-w-2xl">
            The bot posts detailed cards (show logo, description, schedule, season and every host&apos;s photo &amp; bio) for new shows, 1 week before premieres, finales and specials, and before every episode. Pick a channel to turn it on.
          </p>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <label className="text-xs space-y-1">
          <span className="label-eyebrow block">Announcements channel</span>
          <select value={form.show_alerts_channel_id || ""} onChange={(e) => set("show_alerts_channel_id")(e.target.value)} className="px-3 py-2 text-sm w-full" data-testid="show-alerts-channel-select">
            <option value="">— Off (no channel) —</option>
            {channels.map((c) => <option key={c.id} value={c.id}>#{c.name}</option>)}
          </select>
        </label>
        <label className="text-xs space-y-1">
          <span className="label-eyebrow block">Show Alerts role (fans opt in)</span>
          <select value={form.show_alerts_role_id || ""} onChange={(e) => set("show_alerts_role_id")(e.target.value)} className="px-3 py-2 text-sm w-full" data-testid="show-alerts-role-select">
            <option value="">— None —</option>
            {roles.map((r) => <option key={r.id} value={r.id}>@{r.name}</option>)}
          </select>
        </label>
      </div>
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!form.show_episode_reminders} onChange={(e) => set("show_episode_reminders")(e.target.checked)} data-testid="show-alerts-episodes-toggle" />
          Remind before every episode + &quot;LIVE now&quot; post
        </label>
        <label className="inline-flex items-center gap-2">
          <input type="number" min={5} max={180} value={form.show_reminder_lead_minutes} onChange={(e) => set("show_reminder_lead_minutes")(e.target.value)} className="w-20 px-2 py-1" data-testid="show-alerts-lead-input" />
          minutes before
        </label>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <PingSelect label="Ping · New shows" value={form.show_ping_new} onChange={set("show_ping_new")} testId="show-alerts-ping-new" />
        <PingSelect label="Ping · Premieres / finales / specials" value={form.show_ping_premiere} onChange={set("show_ping_premiere")} testId="show-alerts-ping-premiere" />
        <PingSelect label="Ping · Episode reminders" value={form.show_ping_episode} onChange={set("show_ping_episode")} testId="show-alerts-ping-episode" />
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={save} disabled={!dirty} className="btn-primary text-xs disabled:opacity-40" data-testid="show-alerts-save"><Save size={12} /> Save</button>
        <button onClick={postPanel} disabled={dirty || !saved.show_alerts_channel_id || !saved.show_alerts_role_id} className="btn-ghost inline-flex items-center gap-1.5 text-xs disabled:opacity-40" data-testid="show-alerts-post-panel">
          <BellPlus size={12} /> Post &quot;🔔 Show Alerts&quot; opt-in button
        </button>
      </div>
      <p className="text-[0.7rem] text-[var(--ff-text-dim)]">Tip: the bot&apos;s role must sit above the Show Alerts role, and needs &quot;Mention Everyone&quot; in the channel for @everyone pings.</p>
      <Activity data={activity} />
    </div>
  );
}
